import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuthStore } from './store';
import type { AuthenticatedUser } from './types';

/**
 * react-query key factory for the auth domain. Single shared key for
 * `me` so login/logout mutations can invalidate or set it directly.
 */
export const authKeys = {
  all: ['auth'] as const,
  me: () => ['auth', 'me'] as const,
};

/**
 * Fetch the currently authenticated user.
 *
 * - Returns `null` when the backend says 401 (no session) so consumers
 *   can react to the "logged-out" state without try/catch noise.
 * - Re-throws any other error: the global query error boundary renders
 *   a friendly message for them.
 */
async function fetchCurrentUser(): Promise<AuthenticatedUser | null> {
  try {
    const { data } = await api.get<AuthenticatedUser>('/users/me');
    return data;
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 401) return null;
    throw err;
  }
}

/**
 * useCurrentUser — single source of truth for the active session.
 *
 * On every render, syncs the result into the Zustand auth store so the
 * rest of the app (sidebar, role-aware components) can read it without
 * passing the hook around.
 */
export function useCurrentUser() {
  const setUser = useAuthStore((s) => s.setUser);
  const markBootstrapped = useAuthStore((s) => s.markBootstrapped);

  const query = useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchCurrentUser,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (query.isSuccess) {
      setUser(query.data);
    } else if (query.isError) {
      markBootstrapped();
    }
  }, [query.isSuccess, query.isError, query.data, setUser, markBootstrapped]);

  return query;
}
